import { cva, type VariantProps } from 'class-variance-authority';
import { AnimatePresence, motion } from 'motion/react';
import { Slot } from 'radix-ui';
import { type ComponentProps, forwardRef } from 'react';

import { cn } from '../lib/cn.ts';
import { TRANSITION } from '../lib/motion.tsx';
import { Spinner } from './spinner.tsx';

export const buttonVariants = cva(
  [
    'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-bold',
    'border-thick transition-colors select-none',
    'disabled:pointer-events-none disabled:opacity-45',
    '[&_svg]:size-4 [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        /** Главное действие экрана — акцент. */
        primary: 'border-accent bg-accent text-on-accent hover:brightness-95 active:brightness-90',
        /** Обычное действие — обводка цветом текста. */
        secondary: 'border-strong bg-surface text-text hover:bg-surface-2',
        /** Тёмная плашка (инверсия). */
        strong: 'border-strong bg-strong text-on-strong hover:opacity-90',
        /** Действие без рамки: в панелях и таблицах. */
        ghost: 'border-transparent bg-transparent text-text hover:bg-surface-2',
        /** Опасное действие. */
        danger: 'border-danger bg-surface text-danger hover:bg-danger-soft',
      },
      size: {
        sm: 'h-7 px-2.5 text-small',
        md: 'h-8 px-3 text-body',
        lg: 'h-10 px-4 text-lead',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  /** Отрисовать как дочерний элемент (например, ссылку роутера). */
  asChild?: boolean;
  /** Действие выполняется: кнопка недоступна, вместо иконки — индикатор. */
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild = false, loading = false, disabled, children, type, ...props },
  ref,
) {
  const Component = asChild ? Slot.Root : 'button';
  return (
    <Component
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      type={asChild ? undefined : (type ?? 'button')}
      {...props}
    >
      {/* Slot принимает ровно один элемент: у кнопки-ссылки индикатора загрузки нет. */}
      {asChild ? (
        children
      ) : (
        <>
          {/* Индикатор вырастает из нуля вместе с отступом (gap-1.5 = 6 px): кнопка меняет ширину плавно,
              а не прыгает на ширину индикатора, и не двигает соседей по ряду. */}
          <AnimatePresence initial={false}>
            {loading ? (
              <motion.span
                key="spinner"
                aria-hidden="true"
                className="flex shrink-0 overflow-hidden"
                initial={{ width: 0, marginRight: -6, opacity: 0 }}
                animate={{ width: 16, marginRight: 0, opacity: 1 }}
                exit={{ width: 0, marginRight: -6, opacity: 0 }}
                transition={TRANSITION.resize}
              >
                <Spinner />
              </motion.span>
            ) : null}
          </AnimatePresence>
          {children}
        </>
      )}
    </Component>
  );
});

export interface IconButtonProps extends Omit<ButtonProps, 'children'> {
  /** Подпись для экранных дикторов и подсказки — обязательна у кнопки-иконки. */
  label: string;
  icon: React.ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, className, size = 'md', variant = 'ghost', ...props },
  ref,
) {
  return (
    <Button
      ref={ref}
      aria-label={label}
      title={label}
      variant={variant}
      size={size}
      className={cn(size === 'sm' ? 'w-7 px-0' : size === 'lg' ? 'w-10 px-0' : 'w-8 px-0', className)}
      {...props}
    >
      {icon}
    </Button>
  );
});
